/**
 * Document Parser Utility
 * Handles parsing of PDF, TXT, DOC, and DOCX files into HTML format
 */

// Constants
const PDFJS_WORKER_CDN = 'https://unpkg.com/pdfjs-dist';
const PDFJS_DEFAULT_VERSION = '4.8.69';
const PDF_LINE_TOLERANCE = 2; // Y coordinate tolerance for same line
const PDF_PARAGRAPH_GAP = 8; // Minimum Y difference to start new paragraph
const MAX_CONTENT_LENGTH = 10000; // Maximum characters after parsing

// Type definitions
export interface PDFTextItem {
  str: string;
  transform: number[];
  fontName?: string;
  hasEOL?: boolean;
  width?: number;
  height?: number;
}

export interface ParseDocumentResult {
  htmlContent: string;
  error?: string;
  isCorrupted?: boolean;
}

export const SUPPORTED_FILE_TYPES = ['.pdf', '.txt', '.doc', '.docx', '.json'] as const;
export const SUPPORTED_FILE_EXTENSIONS = ['pdf', 'txt', 'doc', 'docx', 'json'] as const;

export type SupportedFileExtension = 'pdf' | 'txt' | 'doc' | 'docx' | 'json';

/**
 * Unified Document Parser
 * Handles PDF, TXT, DOC, and DOCX file formats
 * @param file - The file to parse
 * @returns Promise with HTML content or error message
 */
/**
 * Validate content length
 */
function stripHtmlTagsLinear(html: string, replacement = ''): string {
  const parts: string[] = [];
  let i = 0;
  while (i < html.length) {
    const open = html.indexOf('<', i);
    if (open === -1) {
      parts.push(html.slice(i));
      break;
    }
    parts.push(html.slice(i, open));
    const close = html.indexOf('>', open + 1);
    if (close === -1) {
      parts.push(html.slice(open));
      break;
    }
    if (replacement) {
      parts.push(replacement);
    }
    i = close + 1;
  }
  return parts.join('');
}

function validateContentLength(htmlContent: string): ParseDocumentResult | null {
  // Strip HTML tags to count actual text characters
  const textContent = stripHtmlTagsLinear(htmlContent);
  if (textContent.length > MAX_CONTENT_LENGTH) {
    return {
      htmlContent: '',
      error: `File content exceeds the maximum limit of ${MAX_CONTENT_LENGTH.toLocaleString()} characters. Please use a smaller file.`,
    };
  }
  return null;
}

export async function parseDocument(file: File): Promise<ParseDocumentResult> {
  // Check if file is empty (0 bytes)
  if (file.size === 0) {
    return {
      htmlContent: '',
      error: 'File is empty. Please select a file with content.',
      isCorrupted: true,
    };
  }

  const fileExtension = getFileExtension(file.name);

  if (!fileExtension) {
    return { htmlContent: '', error: 'Unable to read file. Try another format.' };
  }

  // Check if file extension is supported for document parsing (exclude JSON)
  if (!['pdf', 'txt', 'doc', 'docx'].includes(fileExtension)) {
    return { htmlContent: '', error: 'Unable to read file. Try another format.' };
  }

  try {
    let result: ParseDocumentResult;

    switch (fileExtension) {
      case 'pdf':
        result = await parsePDFFile(file);
        break;
      case 'docx':
      case 'doc':
        result = await parseWordFile(file);
        break;
      case 'txt':
        result = await parseTextFile(file);
        break;
      default:
        return { htmlContent: '', error: 'Unable to read file. Try another format.' };
    }

    // Check for empty content (file has no text content)
    if (!result.htmlContent || !result.htmlContent.trim()) {
      // If there's already an error, return it; otherwise it's an empty document
      if (result.error) {
        return result;
      }
      return {
        htmlContent: '',
        error: 'File is empty or contains no readable text content.',
        isCorrupted: true,
      };
    }

    // Validate content length
    if (result.htmlContent) {
      const lengthCheck = validateContentLength(result.htmlContent);
      if (lengthCheck) {
        return lengthCheck;
      }
    }

    return result;
  } catch (error) {
    console.error('Error parsing document:', error);

    // Check for specific corruption errors
    const errorMessage = error instanceof Error ? error.message.toLowerCase() : '';
    if (
      errorMessage.includes('corrupt') ||
      errorMessage.includes('invalid') ||
      errorMessage.includes('unreadable') ||
      errorMessage.includes('malformed')
    ) {
      return { htmlContent: '', error: 'File unreadable', isCorrupted: true };
    }

    return {
      htmlContent: '',
      error: 'File unreadable',
      isCorrupted: true,
    };
  }
}

/**
 * Get file extension from filename
 */
function getFileExtension(filename: string): SupportedFileExtension | null {
  const extension = filename.split('.').pop()?.toLowerCase();
  if (extension && ['pdf', 'txt', 'doc', 'docx', 'json'].includes(extension)) {
    return extension as SupportedFileExtension;
  }
  return null;
}

/**
 * Parse PDF file and extract text with formatting
 */
async function parsePDFFile(file: File): Promise<ParseDocumentResult> {
  // Use legacy build for broader browser/runtime compatibility in app bundles.
  const pdfjsLib = await import('pdfjs-dist/legacy/build/pdf.mjs');
  configurePDFWorker(pdfjsLib);

  const arrayBuffer = await file.arrayBuffer();
  const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });

  // Check for PDF corruption
  const pdf = await loadingTask.promise;

  if (!pdf || pdf.numPages === 0) {
    return { htmlContent: '', error: 'File unreadable', isCorrupted: true };
  }

  let htmlContent = '';

  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    try {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();
      const items = textContent.items as PDFTextItem[];

      const lines = groupTextItemsIntoLines(items);
      const pageHtml = convertLinesToHTML(lines);
      htmlContent += pageHtml;

      // Add page break between pages (except last page)
      if (pageNum < pdf.numPages) {
        htmlContent += '<p><br></p>';
      }
    } catch (pageError) {
      // If a page fails, continue with other pages but mark as potentially corrupted
      console.warn(`Error parsing page ${pageNum}:`, pageError);
    }
  }

  // Check if PDF has no extractable text content
  if (!htmlContent.trim()) {
    return {
      htmlContent: '',
      error: 'File is empty or contains no readable text content.',
      isCorrupted: true,
    };
  }

  return { htmlContent };
}

/**
 * Configure PDF.js worker
 */
function configurePDFWorker(pdfjsLib: {
  GlobalWorkerOptions: { workerSrc?: string };
  version?: string;
}): void {
  if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
    const version = pdfjsLib.version || PDFJS_DEFAULT_VERSION;
    pdfjsLib.GlobalWorkerOptions.workerSrc = `${PDFJS_WORKER_CDN}@${version}/legacy/build/pdf.worker.min.mjs`;
  }
}

/**
 * Group PDF text items into lines based on Y position
 */
function groupTextItemsIntoLines(items: PDFTextItem[]): PDFTextItem[][] {
  const lines: PDFTextItem[][] = [];
  let currentLine: PDFTextItem[] = [];
  let currentLineY: number | null = null;

  for (const item of items) {
    if (!item.str) {
      continue;
    }

    const itemY = item.transform[5]; // Y coordinate from transform matrix

    // Check if this item is on the same line
    if (currentLineY === null || Math.abs(itemY - currentLineY) <= PDF_LINE_TOLERANCE) {
      currentLine.push(item);
      if (currentLineY === null) {
        currentLineY = itemY;
      }
    } else {
      // New line detected
      if (currentLine.length > 0) {
        lines.push([...currentLine]);
      }
      currentLine = [item];
      currentLineY = itemY;
    }
  }

  // Add the last line
  if (currentLine.length > 0) {
    lines.push(currentLine);
  }

  return lines;
}

/**
 * Convert PDF lines to HTML paragraphs with formatting
 */
function convertLinesToHTML(lines: PDFTextItem[][]): string {
  let htmlContent = '';
  let currentParagraph: string[] = [];
  let lastLineY: number | null = null;

  for (const line of lines) {
    if (line.length === 0) {
      continue;
    }

    const lineY = line[0].transform[5];
    const lineText = formatLineText(line);

    if (!lineText) {
      continue;
    }

    // Check if we should start a new paragraph
    if (lastLineY !== null && Math.abs(lineY - lastLineY) > PDF_PARAGRAPH_GAP) {
      if (currentParagraph.length > 0) {
        htmlContent += `<p>${currentParagraph.join(' ')}</p>`;
      }
      currentParagraph = [lineText];
    } else {
      currentParagraph.push(lineText);
    }

    lastLineY = lineY;
  }

  // Add remaining paragraph
  if (currentParagraph.length > 0) {
    htmlContent += `<p>${currentParagraph.join(' ')}</p>`;
  }

  return htmlContent;
}

/**
 * Format a line of text items with HTML formatting tags
 */
function formatLineText(line: PDFTextItem[]): string {
  return line
    .map(item => {
      let text = item.str;
      const fontName = (item.fontName || '').toLowerCase();

      // Detect formatting from font name
      if (fontName.includes('bold') || fontName.includes('black')) {
        text = `<strong>${text}</strong>`;
      } else if (fontName.includes('italic') || fontName.includes('oblique')) {
        text = `<em>${text}</em>`;
      }

      return text;
    })
    .join(' ')
    .trim();
}

/**
 * Parse Word document (.doc, .docx) using mammoth
 */
async function parseWordFile(file: File): Promise<ParseDocumentResult> {
  try {
    const mammothModule = await import('mammoth');
    const mammoth = mammothModule.default || mammothModule;
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.convertToHtml({ arrayBuffer });

    // Check if Word document has no content
    if (!result.value || !result.value.trim()) {
      return {
        htmlContent: '',
        error: 'File is empty or contains no readable text content.',
        isCorrupted: true,
      };
    }

    return { htmlContent: result.value };
  } catch (error) {
    // Check if it's a corruption error
    const errorMessage = error instanceof Error ? error.message.toLowerCase() : '';
    if (errorMessage.includes('corrupt') || errorMessage.includes('invalid')) {
      return { htmlContent: '', error: 'File unreadable', isCorrupted: true };
    }
    throw error;
  }
}

/**
 * Parse plain text file (.txt)
 */
function parseTextFile(file: File): Promise<ParseDocumentResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event: ProgressEvent<FileReader>) => {
      try {
        const textContent = event.target?.result as string;

        // Check if text file is empty
        if (!textContent || !textContent.trim()) {
          resolve({
            htmlContent: '',
            error: 'File is empty or contains no readable text content.',
            isCorrupted: true,
          });
          return;
        }

        const htmlContent = textContent
          .split('\n')
          .map(line => `<p>${line || '<br>'}</p>`)
          .join('');
        resolve({ htmlContent });
      } catch (error) {
        resolve({ htmlContent: '', error: 'File unreadable', isCorrupted: true });
      }
    };
    reader.onerror = () => {
      resolve({ htmlContent: '', error: 'File unreadable', isCorrupted: true });
    };
    reader.readAsText(file);
  });
}
